<script setup lang="ts">
import { onBeforeUnmount, ref } from 'vue';
import { api } from '../../lib/api';

const props = defineProps<{ sceneId: string; selectedIds?: string[]; busy: boolean }>();
const emit = defineEmits<{ update: [ids: string[] | undefined] }>();
const items = ref<Array<{ id: string; label: string }>>([]);
const loaded = ref(false);
const loading = ref(false);
const error = ref('');
let controller: AbortController | null = null;
async function load() {
  if (loaded.value || loading.value) return;
  controller = new AbortController();
  const current = controller;
  loading.value = true;
  error.value = '';
  try {
    const response = await api<{ items: Array<{ id: string; label: string }> }>(
      `/api/v1/entities/${props.sceneId}/scene-segments`,
      { signal: current.signal }
    );
    if (!current.signal.aborted) {
      items.value = response.items;
      loaded.value = true;
    }
  } catch (reason) {
    if (!current.signal.aborted)
      error.value = reason instanceof Error ? reason.message : 'Segmente konnten nicht geladen werden.';
  } finally {
    loading.value = false;
  }
}
function toggle(id: string, checked: boolean) {
  if (props.busy) return;
  const ids = new Set(props.selectedIds ?? items.value.map((item) => item.id));
  if (checked) ids.add(id);
  else ids.delete(id);
  emit(
    'update',
    items.value.filter((item) => ids.has(item.id)).map((item) => item.id)
  );
}
onBeforeUnmount(() => controller?.abort());
</script>

<template>
  <details class="export-segments" @toggle="($event.target as HTMLDetailsElement).open && load()">
    <summary>
      Segmente auswählen · {{ selectedIds === undefined ? 'alle' : `${selectedIds.length} ausgewählt` }}
    </summary>
    <p>Gezielt ausgewählte Segmente werden mit dem Szenentitel exportiert. Der separate Szenentext entfällt dann.</p>
    <span v-if="loading">Segmente werden geladen …</span>
    <div v-if="error" role="alert">
      {{ error }} <button type="button" :disabled="busy" @click="load">Erneut laden</button>
    </div>
    <template v-if="loaded">
      <div class="export-segment-actions">
        <button type="button" :disabled="busy" @click="emit('update', undefined)">Alle Segmente</button>
        <button type="button" :disabled="busy" @click="emit('update', [])">Keine Segmente</button>
      </div>
      <label v-for="item in items" :key="item.id">
        <input
          type="checkbox"
          :checked="selectedIds === undefined || selectedIds.includes(item.id)"
          :disabled="busy"
          @change="toggle(item.id, ($event.target as HTMLInputElement).checked)"
        />{{ item.label }}
      </label>
      <small v-if="!items.length">Diese Szene enthält keine Segmente.</small>
      <small v-if="selectedIds?.some((id) => !items.some((item) => item.id === id))" role="alert"
        >Ein ausgewähltes Segment ist nicht mehr verfügbar. Bitte wähle die Segmente erneut.</small
      >
    </template>
  </details>
</template>

<style scoped>
.export-segments {
  margin: 8px 0;
  font-size: 12px;
}
summary {
  cursor: pointer;
}
p,
small {
  color: var(--ui-text-muted);
  line-height: 1.5;
}
label {
  display: flex;
  align-items: center;
  gap: 7px;
  margin-top: 7px;
}
input {
  width: auto;
  margin: 0;
}
.export-segment-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 8px 0;
}
</style>
